import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { Card, CardHeader, FormRow, NumberInput } from "@components/ui-kit";
import { Trans, t } from "@lingui/macro";
import { Icon } from "@src/components/ui-kit/Icon";
import { can } from "@src/utils/components/permissions";
import { fieldNumberModelToInputProps } from "@src/utils/forms/inputHelpers";
import { minValue } from "@src/utils/forms/validators";
import { useStore } from "@src/utils/hooks";
import type { NumberModel } from "@src/utils/models";
import { FieldState } from "formstate";
import { debounce } from "lodash";
import { observer, useLocalObservable } from "mobx-react-lite";
import React, { type FunctionComponent, useEffect } from "react";

export const CardTimeTrackingRate: FunctionComponent<
  React.PropsWithChildren<{
    average_time_tracking_rate: NumberModel;
    calculatedTimeTrackingRate: NumberModel;
    onAverageTimeTrackingRateChange(val: NumberModel): void;
    onEdit?: () => void;
    bordered?: boolean;
  }>
> = observer(function CardTimeTrackingRate({
  average_time_tracking_rate,
  calculatedTimeTrackingRate,
  onAverageTimeTrackingRateChange,
  onEdit,
  children,
  bordered = true,
}) {
  const { workspaceStore } = useStore();
  const state = useLocalObservable(() => {
    return {
      field: new FieldState(average_time_tracking_rate).validators(minValue(0)),
      onAverageTimeTrackingRateChange: debounce(() => {
        onAverageTimeTrackingRateChange(state.field.$);
      }, 1000),
    };
  });

  useEffect(() => {
    state.field.onDidChange(() => state.onAverageTimeTrackingRateChange());
  }, []);

  return (
    <Card w="full" border={bordered ? undefined : "none"}>
      <CardHeader
        icon="clock-stopwatch"
        title={
          <Heading size="md">
            {workspaceStore.settings?.time_tracking_rate_type === "man-hour"
              ? t`Hourly rates`
              : t`MD rates`}
          </Heading>
        }
        action={
          onEdit &&
          workspaceStore.settings?.moduleSettings.budgetovac &&
          can("integration_budgetovac") && (
            <Button onClick={onEdit}>{t`Edit`}</Button>
          )
        }
      />
      <Card.Section>
        <Card.Subsection>
          <FormRow
            title={
              <Flex align="center">
                <Trans>Average time rate</Trans>
                <Tooltip
                  label={
                    <Flex minW="100px" maxW="300" p="2">
                      {workspaceStore.settings?.time_tracking_rate_type ===
                        "man-hour" && (
                        <Trans>
                          Will be used for hourly estimation of work from
                          budget.
                        </Trans>
                      )}
                      {workspaceStore.settings?.time_tracking_rate_type ===
                        "man-day" && (
                        <Trans>
                          Will be used for MD estimation of work from budget.
                        </Trans>
                      )}
                    </Flex>
                  }
                >
                  <div className="h-full inline-flex items-center ml-2">
                    <Icon
                      name="info-circle"
                      variant="solid"
                      color="#919EAB"
                      style={{ transform: "scale(0.8)" }}
                    />
                  </div>
                </Tooltip>
              </Flex>
            }
          >
            <HStack>
              <Box maxW="150px">
                <NumberInput
                  currency
                  id="field-project-settings-average-time-rate"
                  {...fieldNumberModelToInputProps(state.field)}
                  value={
                    state.field.$.value === 0
                      ? calculatedTimeTrackingRate.value.toString()
                      : state.field.$.value.toString()
                  }
                  suffix={workspaceStore.settings?.currency.code}
                  readOnly={!can("brand_update")}
                />
              </Box>
              <Box ml="4">
                {workspaceStore.settings?.time_tracking_rate_type ===
                  "man-hour" && <Trans>per hour</Trans>}

                {workspaceStore.settings?.time_tracking_rate_type ===
                  "man-day" && <Trans>per MD</Trans>}
              </Box>
            </HStack>
          </FormRow>

          <FormRow
            title={
              <Flex align="center">
                <Trans>Calculated average time rate</Trans>
                <Tooltip
                  label={
                    <Trans>
                      This value will be used for average time rate if average
                      time rate is set to 0.
                    </Trans>
                  }
                >
                  <div className="h-full inline-flex items-center ml-2">
                    <Icon
                      name="info-circle"
                      variant="solid"
                      color="#919EAB"
                      style={{ transform: "scale(0.8)" }}
                    />
                  </div>
                </Tooltip>
              </Flex>
            }
          >
            <HStack align="center">
              <Box maxW="150px">
                <Text>{calculatedTimeTrackingRate?.value ?? 0}</Text>
              </Box>
              <Box ml="4">
                {workspaceStore.settings?.time_tracking_rate_type ===
                  "man-hour" && <Trans>per hour</Trans>}

                {workspaceStore.settings?.time_tracking_rate_type ===
                  "man-day" && <Trans>per MD</Trans>}
              </Box>
            </HStack>
          </FormRow>
        </Card.Subsection>
        {!!React.Children.toArray(children).length && (
          <Card.Subsection>
            <h2 className="uppercase font-semibold mb-8 text-inkLighter text-lg">
              <Trans>Rates per task</Trans>
            </h2>
            <div>{children}</div>
          </Card.Subsection>
        )}
      </Card.Section>
    </Card>
  );
});
